import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Phone, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Product Detail Modal ─── */
function RentalDetail({ item, onClose, lang }) {
  if (!item) return null;

  const scrollToQuote = () => {
    onClose();
    setTimeout(() => document.querySelector("#soumission")?.scrollIntoView({ behavior: "smooth" }), 300);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        data-testid="rental-detail-overlay"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 w-full max-w-3xl bg-[#0a0a0a] border border-white/10 overflow-hidden"
          data-testid="rental-detail-modal"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center bg-black/60 border border-white/10 hover:border-[#E60000] text-white/60 hover:text-white transition-all"
            data-testid="rental-detail-close"
          >
            <X size={18} />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Image */}
            <div className="relative h-64 md:h-full min-h-[300px] overflow-hidden">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0a0a]/30 hidden md:block" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent md:hidden" />
            </div>

            {/* Details */}
            <div className="p-6 md:p-8 flex flex-col justify-between">
              <div>
                {/* Price tag */}
                {item.price && (
                  <div className="inline-block bg-[#E60000] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 mb-4" data-testid="rental-detail-price">
                    {item.price}
                  </div>
                )}

                <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase text-white tracking-tight mb-4" data-testid="rental-detail-title">
                  {item.title}
                </h2>

                <p className="font-body text-sm md:text-base text-[#A3A3A3] leading-relaxed mb-6">
                  {item.description}
                </p>

                {/* Features list */}
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-white/50">
                    <ChevronRight size={14} className="text-[#E60000]" />
                    <span className="font-body text-xs">{lang === "fr" ? "Livraison & installation disponible" : "Delivery & setup available"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/50">
                    <ChevronRight size={14} className="text-[#E60000]" />
                    <span className="font-body text-xs">{lang === "fr" ? "Support technique inclus" : "Technical support included"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/50">
                    <ChevronRight size={14} className="text-[#E60000]" />
                    <span className="font-body text-xs">{lang === "fr" ? "Équipement professionnel haut de gamme" : "High-end professional equipment"}</span>
                  </div>
                </div>
              </div>

              {/* CTA buttons */}
              <div className="space-y-3">
                <button
                  onClick={scrollToQuote}
                  className="w-full flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white text-xs font-bold uppercase tracking-[0.15em] py-4 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/40"
                  data-testid="rental-detail-cta"
                >
                  {lang === "fr" ? "Obtenir un prix" : "Get a quote"}
                  <ArrowRight size={14} />
                </button>
                <a
                  href="tel:+15144330846"
                  className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-[#E60000]/50 text-white/60 hover:text-white text-xs font-bold uppercase tracking-[0.15em] py-3.5 transition-all duration-300"
                >
                  <Phone size={14} />
                  514 433-0846
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ─── Main Rentals Section ─── */
export default function Rentals() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content/rentals`).then(r => setItems(r.data)).catch(e => console.error("Rentals fetch error:", e));
  }, []);

  if (!items.length) return null;

  const scrollTo = () => document.querySelector("#soumission")?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      <section id="location" className="relative py-16 md:py-32 bg-[#0a0a0a]" data-testid="rentals-section">
        <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-[#E60000]/5 rounded-full blur-[200px]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
          <div className="mb-10 md:mb-16 text-center">
            <span className="text-[#E60000] text-xs sm:text-sm uppercase tracking-[0.3em] font-medium font-body">
              {lang === "fr" ? "Location d'équipement" : "Equipment Rental"}
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold uppercase text-white mt-3" data-testid="rentals-title">
              {lang === "fr" ? "Louez le" : "Rent the"} <span className="text-[#E60000]">{lang === "fr" ? "meilleur." : "best."}</span>
            </h2>
            <p className="font-body text-sm sm:text-base text-[#A3A3A3] mt-4 max-w-2xl mx-auto">
              {lang === "fr"
                ? "Équipement professionnel disponible en location. Cliquez sur un équipement pour voir les détails."
                : "Professional equipment available for rent. Click on any item to see details."}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {items.map((item, i) => (
              <motion.div
                key={item.id || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                onClick={() => setSelectedItem(item)}
                className="group relative overflow-hidden border border-white/5 hover:border-[#E60000]/50 bg-[#121212]/50 transition-all duration-500 hover:translate-y-[-4px] hover:shadow-xl hover:shadow-red-900/20 cursor-pointer"
                data-testid={`rental-${i}`}
              >
                {/* Image */}
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                  {item.price && (
                    <div className="absolute top-3 right-3 bg-[#E60000] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1">
                      {item.price}
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-[#E60000]/0 group-hover:bg-[#E60000]/10 transition-all duration-500 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-2 border border-white/20">
                      {lang === "fr" ? "Voir détails" : "View details"}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-5">
                  <h3 className="font-heading text-sm sm:text-base font-bold uppercase text-white mb-2 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="font-body text-xs sm:text-sm text-[#A3A3A3] leading-relaxed line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block border border-[#E60000]/20 bg-[#E60000]/5 px-6 py-4">
              <p className="font-body text-sm text-white/70 mb-2">
                {lang === "fr"
                  ? "Besoin d'un combo ou d'une configuration complète?"
                  : "Need a combo or complete setup?"}
              </p>
              <button
                onClick={scrollTo}
                className="bg-[#E60000] hover:bg-[#FF3333] text-white text-xs sm:text-sm font-bold uppercase tracking-[0.15em] px-8 py-3 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/40"
                data-testid="rentals-quote-cta"
              >
                {lang === "fr" ? "Obtenir un prix sur mesure" : "Get a custom quote"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      {selectedItem && (
        <RentalDetail item={selectedItem} onClose={() => setSelectedItem(null)} lang={lang} />
      )}
    </>
  );
}
